/**
 * RegistrationProgramPanel - Program column for developer/demo landing.
 * Uses CSS Grid subgrid (md+) for pixel-perfect section alignment across panels.
 * On mobile, falls back to flex column layout.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { ECGridColors } from '@/types/colors';
import { RegistrationPricingTable } from './RegistrationPricingTable';
import { buildPanelData, type PanelVariant } from './registration-panel-content';

export type { PanelVariant };

const BADGE_STYLES = {
  van: { backgroundColor: '#E6F1FB', color: '#0C447C' },
  simplify: { backgroundColor: '#E1F5EE', color: '#0F6E56' },
} as const;

const sectionHeaderClass = 'text-xs font-bold uppercase tracking-widest mb-2';
const sectionHeaderStyle = { color: '#9C9A92' };

interface Props {
  variant: PanelVariant;
  onCtaClick?: () => void;
  isCtaDisabled?: boolean;
}

/**
 * 8 grid rows (subgrid on md+):
 *  1. Header (badge + title + descriptions)
 *  2. Capabilities + tier intros
 *  3. Tier name
 *  4. Pricing
 *  5. What's included
 *  6. Limitations (empty placeholder for developer panels)
 *  7. CTA block
 *  8. Not-ready box
 */
export const RegistrationProgramPanel: React.FC<Props> = ({
  variant,
  onCtaClick,
  isCtaDisabled = false,
}) => {
  const { t } = useTranslation(NS.DEVELOPER_REGISTRATION);
  const data = buildPanelData(variant, t as (key: string, fallback?: string) => string);
  const badgeStyle = BADGE_STYLES[data.badgeStyle];

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex flex-col gap-4 md:grid md:grid-rows-subgrid md:row-span-8"
      style={{ rowGap: '1rem' }}
    >
      {/* Row 1: Header */}
      <div className="flex flex-col gap-3">
        <span className="inline-block text-xs font-bold px-2.5 py-1 rounded self-start tracking-wide" style={badgeStyle}>
          {data.badge}
        </span>
        <h2 className="text-xl font-bold leading-tight" style={{ color: ECGridColors.tealDark }}>
          {data.title}
        </h2>
        <div className="space-y-2 text-sm text-gray-600 leading-relaxed">
          {data.descriptions.map((d, i) => <p key={i}>{d}</p>)}
        </div>
      </div>

      {/* Row 2: Capabilities + tier intros */}
      <div className="flex flex-col gap-3">
        {data.capabilities && data.capabilities.length > 0 && (
          <ul className="list-disc list-outside pl-5 space-y-1 text-sm text-gray-700 marker:text-blue-600">
            {data.capabilities.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        )}
        {data.tierIntros && data.tierIntros.length > 0 && (
          <div className="space-y-2 text-sm text-gray-600 leading-relaxed">
            {data.tierIntros.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        )}
      </div>

      {/* Row 3: Tier name */}
      <p className="text-base font-semibold self-end" style={{ color: ECGridColors.navy }}>
        {data.tierName}
      </p>

      {/* Row 4: Pricing */}
      <div>
        <p className={sectionHeaderClass} style={sectionHeaderStyle}>PRICING</p>
        <RegistrationPricingTable rows={data.pricingRows} />
      </div>

      {/* Row 5: What's included */}
      <div>
        <p className={sectionHeaderClass} style={sectionHeaderStyle}>{data.includedHeader}</p>
        <ul className="list-disc list-outside pl-5 space-y-1 text-sm text-gray-700 marker:text-blue-600">
          {data.included.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </div>

      {/* Row 6: Limitations (empty placeholder for developer panels to keep grid aligned) */}
      {data.limitations && data.limitationsHeader ? (
        <div>
          <p className={sectionHeaderClass} style={sectionHeaderStyle}>{data.limitationsHeader}</p>
          <ul className="list-disc list-outside pl-5 space-y-1 text-sm text-amber-800 marker:text-amber-500">
            {data.limitations.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </div>
      ) : (
        <div aria-hidden="true" />
      )}

      {/* Row 7: CTA */}
      <div className="flex flex-col gap-2">
        <p className="text-sm text-gray-600 leading-relaxed">{data.ctaDescription}</p>
        {data.ctaHref ? (
          <a href={data.ctaHref} target="_blank" rel="noopener noreferrer"
            className="block w-full text-center py-3 px-4 text-white font-medium rounded-md transition-colors duration-200"
            style={{ backgroundColor: ECGridColors.blue }}>
            {data.ctaLabel}
          </a>
        ) : (
          <button type="button" onClick={onCtaClick} disabled={isCtaDisabled}
            className="w-full py-3 px-4 text-white font-medium rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: isCtaDisabled ? '#9ca3af' : ECGridColors.blue }}>
            {data.ctaLabel}
          </button>
        )}
      </div>

      {/* Row 8: Not Ready / Ready to Build */}
      <div className="rounded-md border border-gray-200 p-4 text-sm" style={{ backgroundColor: '#F5F5F4' }}>
        <p className="font-semibold mb-1" style={{ color: ECGridColors.navy }}>{data.notReadyTitle}</p>
        <p className="text-gray-600 leading-relaxed mb-2">{data.notReadyDescription}</p>
        <a href={data.notReadyHref} className="underline font-medium hover:opacity-80 transition-opacity" style={{ color: ECGridColors.blue }}>
          {data.notReadyLinkText}
        </a>
      </div>
    </div>
  );
};
